import DEGULAR_BOLD_FONT_URL from "../assets/fonts/degular-bold-base64";

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const EVENTS_DIR = process.env.REACT_APP_CLOUDINARY_EVENTS_DIR;

export const uploadImage = (data, onSuccess, onConfirmTxn, onError) => {
  fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: "POST",
    body: data,
  })
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      throw new Error("Network response was not ok.");
    })
    .then((data) => onSuccess(data))
    .catch((err) => {
      console.error(err);
      onError && onError(err);
    })
    .finally(() => {
      onConfirmTxn();
    });
};

const getBannerImageOverlay = (bannerImage = "") => {
  if (typeof bannerImage !== "string" || bannerImage.length <= 0) return;
  return bannerImage.split("/").pop().split(".")[0];
};

// TODO rename banner image on upload
// todo figure out a way to not allow uploading duplicate images for a banner. Maybe add "update event" feature.

const getUploadedPictureId = (imageId = "") => {
  if (typeof imageId !== "string" || imageId.length <= 0) return;
  return imageId.replaceAll("/", ":");
};

const BASE_URL = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/`;
export const generateBannerImageUrl = (
  publicId,
  imageAttribute,
  nameAttribute = null,
  userName = "",
  avatarShape = "CIRCLE",
  bannerUrl = ""
) => {
  const uploadedPictureId = getUploadedPictureId(publicId);
  const bannerImageOverlay = getBannerImageOverlay(bannerUrl);
  const imageHeight = imageAttribute?.height || "260";
  const imageWidth = imageAttribute?.width || "260";
  const radius =
    avatarShape?.toLowerCase() === "circle"
      ? "max"
      : imageAttribute?.radius || "0";

  const fontFamily = nameAttribute?.fontFamily || "Lato";
  const fontSize = nameAttribute?.fontSize || "30";
  const fontWeight = nameAttribute?.fontWeight || "bold";
  const fontStyle = nameAttribute?.fontStyle
    ? `_${nameAttribute?.fontStyle}`
    : "";
  const textAlign = "center";
  const nameHeight = nameAttribute?.height || "40";
  const nameWidth = nameAttribute?.width || "703";
  const nameMode = nameAttribute?.textAlign || "lpad";

  if (nameAttribute?.textTransform === "uppercase") {
    userName = userName.toLocaleUpperCase();
  }

  let textColor = nameAttribute?.textColor || "black";
  if (textColor.includes('#')) {
    const textColorRbg = textColor.replaceAll('#', '');
    textColor = `rgb:${textColorRbg}`;
  }

  return !nameAttribute
    ? `${BASE_URL}c_scale,w_703/l_${uploadedPictureId},w_${imageWidth},h_${imageHeight},g_${
        imageAttribute?.position ?? "north_west"
      },x_${imageAttribute?.x ?? "0"},y_${
        imageAttribute?.y ?? "0"
      },r_${radius}/v1658173385/${EVENTS_DIR}/${bannerImageOverlay}.png`
    : `${BASE_URL}c_scale,w_703/l_${uploadedPictureId},w_${imageWidth},h_${imageHeight},g_${
        imageAttribute?.position ?? "north_west"
      },x_${imageAttribute?.x ?? "0"},y_${
        imageAttribute?.y ?? "0"
      },r_${radius}/co_${textColor},g_${
        nameAttribute?.position ?? "north_west"
      },c_${nameMode},w_${nameWidth},h_${nameHeight},l_text:${fontFamily}_${fontSize}_${fontWeight}_${textAlign}${fontStyle}:${userName},x_${
        nameAttribute?.x ?? "0"
      },y_${
        nameAttribute?.y ?? "0"
      }/v1658173385/${EVENTS_DIR}/${bannerImageOverlay}.png`;
};

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false }
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  // croppedAreaPixels values are bounding box relative
  // extract the cropped image using these values
  const data = ctx.getImageData(
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height
  );

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image at the top left corner
  ctx.putImageData(data, 0, 0);

  // As a blob
  const blobUrl = await new Promise((resolve, reject) => {
    canvas.toBlob((file) => {
      resolve(URL.createObjectURL(file));
    }, "image/jpeg");
  });

  // As Base64 string
  const file = canvas.toDataURL("image/jpeg");

  return [blobUrl, file];
}

export async function generateBannerOnCanvas({
  bannerUrl,
  avatarImageUrl,
  userName,
  imageAttribute,
  nameAttribute,
  avatarShape = "CIRCLE",
}) {
  const bannerImg = await createImage(bannerUrl);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const scale = 703 / bannerImg.width;
  canvas.width = 703;
  canvas.height = Math.round(bannerImg.height * scale);
  ctx.drawImage(bannerImg, 0, 0, canvas.width, canvas.height);

  const avatarImg = await createImage(avatarImageUrl);
  const avatarX = imageAttribute?.x || 0;
  const avatarY = imageAttribute?.y || 0;
  const avatarW = imageAttribute?.width || 260;
  const avatarH = imageAttribute?.height || 260;
  const radius = imageAttribute?.radius || 0;

  ctx.save();
  if (avatarShape === "CIRCLE") {
    const size = Math.min(avatarW, avatarH);
    ctx.beginPath();
    ctx.arc(avatarX + size / 2, avatarY + size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(avatarImg, avatarX, avatarY, size, size);
  } else if (radius > 0) {
    ctx.beginPath();
    ctx.roundRect(avatarX, avatarY, avatarW, avatarH, radius);
    ctx.clip();
    ctx.drawImage(avatarImg, avatarX, avatarY, avatarW, avatarH);
  } else {
    ctx.drawImage(avatarImg, avatarX, avatarY, avatarW, avatarH);
  }
  ctx.restore();

  if (nameAttribute && userName) {
    const degularFont = new FontFace("Degular", `url(${DEGULAR_BOLD_FONT_URL})`);
    await degularFont.load();
    document.fonts.add(degularFont);

    const fontSize = Math.max(parseInt(nameAttribute.fontSize) || 30, 48);
    const fontWeight = nameAttribute.fontWeight || "bold";
    const fontStyle = nameAttribute.fontStyle || "normal";
    const textColor = nameAttribute.textColor || "#000000";
    const displayName =
      nameAttribute.textTransform === "uppercase"
        ? userName.toUpperCase()
        : userName;

    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px 'Degular', sans-serif`;
    ctx.fillStyle = textColor.startsWith("#") ? textColor : `#${textColor}`;
    ctx.textBaseline = "middle";

    const textX = nameAttribute.x || 0;
    const textY = nameAttribute.y || 0;
    const textW = nameAttribute.width || 703;
    const textH = nameAttribute.height || 40;

    if (nameAttribute.textAlign === "rpad") {
      ctx.textAlign = "right";
      ctx.fillText(displayName, textX + textW, textY + textH / 2);
    } else if (nameAttribute.textAlign === "lpad") {
      ctx.textAlign = "left";
      ctx.fillText(displayName, textX, textY + textH / 2);
    } else {
      ctx.textAlign = "center";
      ctx.fillText(displayName, textX + textW / 2, textY + textH / 2);
    }
  }

  return canvas.toDataURL("image/jpeg", 0.9);
}
